import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import BroadcastConfirmDialog from '../../components/admin/BroadcastConfirmDialog';
import {
  fetchSegmentCount,
  sendBroadcast,
  resetForm as resetBroadcastForm,
} from '../../store/slices/broadcastSlice';
import config from '../../config';

const validationSchema = Yup.object({
  title: Yup.string()
    .trim()
    .required('Title is required')
    .max(config.broadcast.titleMaxLength, `Max ${config.broadcast.titleMaxLength} characters`),
  body: Yup.string()
    .trim()
    .required('Body is required')
    .max(config.broadcast.bodyMaxLength, `Max ${config.broadcast.bodyMaxLength} characters`),
  segment: Yup.string()
    .required('Audience is required')
    .oneOf(config.broadcast.segments.map(s => s.key), 'Invalid segment'),
});

const initialValues = { title: '', body: '', segment: '' };

const BroadcastPage = () => {
  const dispatch = useDispatch();
  const {
    segmentCount,
    segmentCountLoading,
    segmentCountError,
    sending,
  } = useSelector((state) => state.broadcast);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const handleSegmentChange = (segment, setFieldValue) => {
    setFieldValue('segment', segment);
    if (segment) {
      dispatch(fetchSegmentCount(segment));
    }
  };

  const handleSendClick = (values) => {
    setPendingPayload(values);
    setConfirmOpen(true);
  };

  const handleConfirmSend = (resetFormik) => {
    setConfirmOpen(false);
    dispatch(sendBroadcast(pendingPayload))
      .unwrap()
      .then((result) => {
        const { recipients_count, failed_count } = result;
        if (failed_count === 0) {
          setSnack({
            open: true,
            message: `Notification queued for delivery to ${recipients_count.toLocaleString()} users`,
            severity: 'success',
          });
        } else if (recipients_count > failed_count) {
          setSnack({
            open: true,
            message: `Notification queued for ${(recipients_count - failed_count).toLocaleString()} users (${failed_count} failed)`,
            severity: 'warning',
          });
        } else {
          setSnack({ open: true, message: 'Failed to send notification', severity: 'error' });
        }
        resetFormik();
        dispatch(resetBroadcastForm());
      })
      .catch((error) => {
        setSnack({
          open: true,
          message: `Failed to send notification — ${error}`,
          severity: 'error',
        });
      });
    setPendingPayload(null);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setPendingPayload(null);
  };

  return (
    <AdminLayout title="Push Notifications">
      <Container maxWidth="sm">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Send Push Notification
          </Typography>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={() => {}}
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isValid, dirty, resetForm: resetFormik }) => (
              <Form>
                <FormControl
                  fullWidth
                  error={touched.segment && Boolean(errors.segment)}
                  sx={{ mb: 3 }}
                >
                  <InputLabel>Target audience</InputLabel>
                  <Select
                    name="segment"
                    value={values.segment}
                    label="Target audience"
                    onChange={(e) => handleSegmentChange(e.target.value, setFieldValue)}
                    onBlur={handleBlur}
                    displayEmpty
                  >
                    {config.broadcast.segments.map((seg) => (
                      <MenuItem key={seg.key} value={seg.key}>
                        {seg.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.segment && errors.segment && (
                    <FormHelperText>{errors.segment}</FormHelperText>
                  )}
                </FormControl>

                <Box sx={{ mt: 1, mb: 2, minHeight: 24 }}>
                  {segmentCountLoading && (
                    <Typography variant="body2" color="text.secondary">
                      <CircularProgress size={14} sx={{ mr: 1 }} /> Counting...
                    </Typography>
                  )}
                  {!segmentCountLoading && segmentCount !== null && segmentCount > 0 && (
                    <Typography variant="body2" color="text.secondary" aria-live="polite">
                      {segmentCount.toLocaleString()} users match
                    </Typography>
                  )}
                  {!segmentCountLoading && segmentCount === 0 && (
                    <Typography variant="body2" color="warning.main" aria-live="polite">
                      No users match this segment
                    </Typography>
                  )}
                  {!segmentCountLoading && segmentCountError && (
                    <Typography variant="body2" color="error.main">
                      Unable to load count
                    </Typography>
                  )}
                </Box>

                <TextField
                  name="title"
                  label="Notification title"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.title && Boolean(errors.title)}
                  helperText={
                    touched.title && errors.title
                      ? errors.title
                      : `${values.title.length}/${config.broadcast.titleMaxLength}`
                  }
                  inputProps={{ maxLength: config.broadcast.titleMaxLength }}
                  fullWidth
                  sx={{ mb: 3 }}
                />

                <TextField
                  name="body"
                  label="Notification body"
                  value={values.body}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.body && Boolean(errors.body)}
                  helperText={
                    touched.body && errors.body
                      ? errors.body
                      : `${values.body.length}/${config.broadcast.bodyMaxLength}`
                  }
                  inputProps={{ maxLength: config.broadcast.bodyMaxLength }}
                  multiline
                  rows={3}
                  fullWidth
                  sx={{ mb: 3 }}
                />

                <Button
                  variant="contained"
                  color="primary"
                  startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  onClick={() => handleSendClick(values)}
                  disabled={!isValid || !dirty || segmentCount === null || segmentCount === 0 || sending}
                  fullWidth
                  size="large"
                  sx={{ mt: 2 }}
                >
                  {sending ? 'Sending...' : 'Send'}
                </Button>

                <BroadcastConfirmDialog
                  open={confirmOpen}
                  recipientCount={segmentCount || 0}
                  onCancel={handleConfirmClose}
                  onConfirm={() => handleConfirmSend(resetFormik)}
                  sending={sending}
                />
              </Form>
            )}
          </Formik>
        </Box>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack({ ...snack, open: false })}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </AdminLayout>
  );
};

export default BroadcastPage;
